import Movie from "./Movie";
import { useEffect, useState } from "react";
import {
  useCancelReserveShowtime,
  useReserveShowtime,
} from "../../hooks/useCheckoutQueries";
import { useCheckoutStore } from "../../store/useCheckoutStore";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTimerStore } from "../../context/useTimerStore";
import { statusModalStore } from "../../context/statusModalStore.js";
import { userStore } from "../../store/userStore";
import { useSettingsStore } from "../../context/useSettingsStore.js";
import { authModalStore } from "../../context/authModalStore.js";
import { useRedebanStore } from "../../store/useRedebanStore.js";
import PayStatus from "../PayStatus/PayStatus";
import Room from "./Room/Room.jsx";
import Confectioneries from "./Confectioneries.jsx";
import Locations from "./Locations";
import Checkout from "../Checkout/Checkout";
import Login from "../Vizionario/Login.jsx";
import "../Movie/movie.css";
import "./showtimeDetail.css";
import { useVizionarioStore } from "../../store/useVizionarioStore.js";

export default function ShowtimeDetail() {
  const inProgress = useRedebanStore((state) => state.inProgress);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userMembershipStatus: membership } = userStore();
  const {
    bookingDetails: {
      room,
      room_status: roomStatus,
      showtime,
      movie,
      prices,
      location,
      confectioneryCombos,
    },
    selectedSeats,
    setSelectedSeats,
    setBookingDetails,
  } = useCheckoutStore();
  const showLogin = useVizionarioStore((state) => state.showLogin);
  const isVizionario = useVizionarioStore((state) => state.isVizionario);
  const { setShowLogin } = useVizionarioStore.getState();

  const { handleOpen } = authModalStore();

  const { maxSeats } = useSettingsStore();
  const { startTimer, resetTimer, stopTimer } = useTimerStore();
  const reserveMutation = useReserveShowtime();
  const cancelReserveMutation = useCancelReserveShowtime();

  const [step, setStep] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    if (confectioneryCombos === undefined) {
      setBookingDetails({ confectioneryCombos: [] });
    }
  }, [confectioneryCombos, setBookingDetails]);

  const onFinishTimer = () => {
    stopTimer();
    resetTimer();
    setShowCheckout(false);
    setStep(0);
    setBookingDetails({ confectioneryCombos: [] });
    handleCancelReservation();
  };

  const handleReservation = () => {
    if (!showtime) return;

    const { function_date, function_hour } = showtime;

    const [year, month, day] = function_date.split("-").map(Number);
    const [hours, minutes, seconds] = function_hour.split(":").map(Number);

    const showtimeDate = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      seconds
    );

    if (new Date() > showtimeDate) {
      statusModalStore.getState().showModal({
        title: "Lo sentimos",
        subtitle:
          "No es posible reservar asientos en funciones en curso. Por favor, selecciona otro horario.",
        statusType: "warning", // o "warning" | "error"
        actionText: "Aceptar",
      });
      return;
    }

    // Si la función aún no inició, se procede con la reserva
    reserveMutation.mutate(showtime?.id, {
      onSuccess: () => {
        setStep(1);
        startTimer(() => onFinishTimer());
        setShowCheckout(true);
      },
    });
  };

  const handleCancelReservation = () => {
    cancelReserveMutation.mutate(showtime?.id, {
      onSuccess: () => {
        queryClient.invalidateQueries(["ShowtimeData"]);
      },
    });
  };

  if (!movie || !prices || !room || !showtime || !roomStatus) {
    navigate("/404");
    return null;
  }

  return (
    <>
      {inProgress && <PayStatus />}
      {showLogin && <Login />}
      <div className="showtimeDetail-container">
        {step === 0 && (
          <div className="showtimeDetail-room">
            <div className="container-fluid">
              <div className="row">
                <div className="col-3">
                  <div className="movie-poster">
                    <div className="movie-poster-header" />
                    <img src={movie.poster} alt={movie.title} />
                  </div>
                </div>
                <div className="col-9">
                  {!showCheckout && (
                    <Movie movie={movie} showtime={showtime} step={step} />
                  )}
                </div>
                <div className="col-12">
                  {membership &&
                    membership.Estado.toLowerCase() !==
                      "usuario con tarjeta activa" && (
                      <button
                        onClick={handleOpen}
                        className="showtimeVizButton"
                      >
                        ¿Ya eres vizionario? inicia sesión aquí
                      </button>
                    )}
                  {!isVizionario && (
                    <button onClick={() => setShowLogin(true)} className="vizionarioButton">
                      ¿Eres miembro del <strong>Club Vizionarios</strong>? Inicia sesión aquí
                      <br />
                      <small>
                        (Si no tienes una cuenta, adquiérela en la taquilla)
                      </small>
                    </button>
                  )}
                  <Locations
                    onClick={(value) => setBookingDetails(value)}
                    currentValue={location}
                    locations={prices}
                  />
                  {location && (
                    <div>
                      <Room
                        location={location}
                        room={room}
                        roomStatus={roomStatus}
                        seats={selectedSeats}
                        setSeats={setSelectedSeats}
                        maxSeats={maxSeats}
                      />
                      {selectedSeats.length > 0 && (
                        <div className="showtimeDetail-steps-menu">
                          <button onClick={handleReservation}>
                            Continuar con la compra
                            <span className="icon-arrow-circle-right" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="showtimeDetail-products">
            <div className="showtimeDetail-confectionery">
              <div className="showtimeDetail-confectionery-list">
                <div className="showtimeDetail-steps-menu">
                  <button onClick={onFinishTimer}>
                    <span className="icon-arrow-circle-left" />
                    <div>
                      Cambiar datos anteriores
                      <br />
                      <small>
                        (Deberás volver a seleccionar tu confitería)
                      </small>
                    </div>
                  </button>
                </div>
                <Confectioneries setShowCheckout={setShowCheckout} />
              </div>
              <Checkout onClose={() => setShowCheckout(false)} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
